import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router';
import config from '../utils/config';
import Button from '../components/ui/Button';
import AuthPageLayout from '../components/AuthPageLayout';
import { Input } from '../components/pilot-ui/input';
import { Label } from '../components/pilot-ui/label';

// Plan 28: public forgot-password page. The backend always answers with the
// same generic 200 (enumeration guard) — mirror that here by showing the
// success state no matter what.

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${config.accountsApiUrl}/accounts/forgot-password`, { email });
    } catch {
      // Deliberately swallowed — same message either way.
    } finally {
      setSubmitting(false);
      setSubmitted(true);
    }
  };

  return (
    <AuthPageLayout title="Forgot password">
      {submitted ? (
        <div>
          <p className="mb-4 text-foreground" role="status">
            If an account exists for that email, we've sent a reset link. Check your inbox.
          </p>
          <Link to="/login" className="text-blue-700 underline" title="Return to the console login page">
            Back to login
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <Label htmlFor="forgot-email">Email</Label>
            <p id="forgot-email-help" className="mb-2 text-sm text-muted-foreground">We will send a reset link if this address has a console account.</p>
            <Input
              id="forgot-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              aria-describedby="forgot-email-help"
            />
          </div>
          <Button
            variant="primary"
            type="submit"
            disabled={submitting}
            className="w-full"
            title="Email a link to this address that lets you choose a new password"
          >
            {submitting ? 'Sending…' : 'Send reset link'}
          </Button>
          <div className="mt-3">
            <Link to="/login" className="text-sm text-blue-700 underline" title="Return to the console login page">
              Back to login
            </Link>
          </div>
        </form>
      )}
    </AuthPageLayout>
  );
};

export default ForgotPasswordPage;
